package com.castly.admin.ui.actions

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AuditLogEntryDto
import kotlinx.browser.document
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/**
 * Slide-over detail panel for a single audit log entry.
 *
 * Displays change payload structured by action type:
 *  - Update  → diff table (Field | Before | After)
 *  - Create/Delete → snapshot table (Field | Value)
 *  - Import/BulkUpdate → import summary + optional error list
 *  - Other → raw JSON or metadata only
 *
 * Closes on backdrop click or Escape key.
 */
@Composable
fun AuditLogDetailPanel(
    entry: AuditLogEntryDto?,
    loading: Boolean,
    onClose: () -> Unit
) {
    val colors = AppTheme.colors

    // Escape key closes the panel
    DisposableEffect(Unit) {
        val handler: (Event) -> Unit = { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") onClose()
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    // Full-screen overlay: backdrop (left, closes on click) + panel (right, fixed width)
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            property("z-index", "9999")
        }
    }) {
        // Semi-transparent backdrop — click to close
        Div({
            onClick { onClose() }
            style {
                flex(1)
                backgroundColor(Color("rgba(0,0,0,0.45)"))
            }
        })

        // Side panel
        Div({
            style {
                width(520.px)
                property("max-width", "90vw")
                backgroundColor(Color(colors.surface))
                height(100.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("box-shadow", "-4px 0 24px rgba(0,0,0,0.15)")
                property("border-left", "1px solid ${colors.border}")
                themeTransition()
            }
        }) {
            // Panel header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    padding(Spacing.lg, Spacing.xl)
                    property("border-bottom", "1px solid ${colors.border}")
                    property("flex-shrink", "0")
                    backgroundColor(Color(colors.surfaceHover))
                    themeTransition()
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(Spacing.xs)
                    }
                }) {
                    H2({
                        style {
                            margin(0.px)
                            fontSize(Typography.lg)
                            fontWeight("600")
                            color(Color(colors.text))
                        }
                    }) {
                        Text(if (entry != null) "Action Detail" else "Loading…")
                    }
                    if (entry != null) {
                        Span({
                            style {
                                fontSize(Typography.sm)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            Text("${entry.actionType} · ${entry.entityType}${entry.entityName?.let { " · $it" } ?: ""}")
                        }
                    }
                }

                Button({
                    onClick { onClose() }
                    style {
                        backgroundColor(Color("transparent"))
                        border(0.px)
                        fontSize(Typography.xxl)
                        cursor("pointer")
                        color(Color(colors.textMuted))
                        padding(0.px)
                        width(32.px)
                        height(32.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        borderRadius(BorderRadius.sm)
                    }
                }) { Text("\u00D7") }
            }

            // Panel body — scrollable
            Div({
                style {
                    flex(1)
                    overflow("auto")
                    padding(Spacing.xl)
                }
            }) {
                if (loading) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            padding(48.px)
                            color(Color(colors.textMuted))
                        }
                    }) { Text("Loading entry details…") }
                } else if (entry == null) {
                    Div({
                        style {
                            color(Color(colors.textMuted))
                            fontSize(Typography.base)
                        }
                    }) { Text("Entry not found.") }
                } else {
                    EntryDetailContent(entry)
                }
            }
        }
    }
}

@Composable
private fun EntryDetailContent(entry: AuditLogEntryDto) {
    val colors = AppTheme.colors
    val payload = entry.changePayload as? JsonObject
    val actionLower = entry.actionType.lowercase()

    // Metadata row (timestamp, actor)
    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.md)
            padding(Spacing.lg)
            marginBottom(Spacing.lg)
            display(DisplayStyle.Grid)
            property("grid-template-columns", "auto 1fr")
            property("gap", "${Spacing.sm} ${Spacing.lg}")
            themeTransition()
        }
    }) {
        MetaRow("Timestamp", formatTimestamp(entry.createdAt), colors.textMuted)
        MetaRow("Actor", entry.actorDisplayName, if (entry.actorDisplayName == "[Hidden]") colors.textMuted else colors.text)
        MetaRow("Action", entry.actionType, colors.text)
        MetaRow("Entity", "${entry.entityType}${entry.entityId?.let { " ($it)" } ?: ""}", colors.textMuted)
        val entityName = entry.entityName
        if (entityName != null) {
            MetaRow("Name", entityName, colors.text)
        }
    }

    // Payload section
    when {
        actionLower == "update" -> UpdateDiffSection(payload)
        actionLower in listOf("create", "delete") -> SnapshotSection(entry.actionType, payload)
        actionLower in listOf("import", "bulkupdate") -> ImportSummarySection(payload)
        payload != null -> RawPayloadSection(payload)
    }

    // Session metadata (collapsible)
    val sessionIp = entry.sessionIp
    if (sessionIp != null) {
        SessionMetadataSection(sessionIp)
    }
}

@Composable
private fun UpdateDiffSection(payload: JsonObject?) {
    val colors = AppTheme.colors
    val changes = payload?.get("changes") as? JsonArray

    SectionHeader("Changes")

    if (changes == null || changes.isEmpty()) {
        EmptyPayloadNote("No field changes recorded.")
        return
    }

    Div({
        style {
            border(1.px, LineStyle.Solid, Color(colors.border))
            borderRadius(BorderRadius.md)
            overflow("hidden")
        }
    }) {
        // Table header
        DiffTableHeader()

        // Rows
        changes.forEachIndexed { idx, element ->
            val change = element as? JsonObject ?: return@forEachIndexed
            val field = (change["field"] as? JsonPrimitive)?.content ?: ""
            val oldVal = change["old"]?.let { jsonValueToString(it) } ?: "—"
            val newVal = change["new"]?.let { jsonValueToString(it) } ?: "—"

            if (idx > 0) {
                Div({ style { height(1.px); backgroundColor(Color(colors.border)) } })
            }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr 1fr 1fr")
                    padding(Spacing.md, Spacing.lg)
                }
            }) {
                Span({
                    style {
                        fontSize(Typography.sm)
                        fontWeight("600")
                        color(Color(colors.text))
                    }
                }) { Text(field) }
                DiffCell(oldVal, isOld = true)
                DiffCell(newVal, isOld = false)
            }
        }
    }
}

@Composable
private fun DiffTableHeader() {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr 1fr")
            padding(Spacing.sm, Spacing.lg)
            backgroundColor(Color(colors.surfaceHover))
            property("border-bottom", "1px solid ${colors.border}")
        }
    }) {
        listOf("Field", "Before", "After").forEach { h ->
            Span({
                style {
                    fontSize(Typography.xs)
                    fontWeight("600")
                    color(Color(colors.textMuted))
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.05em")
                }
            }) { Text(h) }
        }
    }
}

@Composable
private fun DiffCell(value: String, isOld: Boolean) {
    val colors = AppTheme.colors
    val isRedacted = value == "[REDACTED]"
    val (bg, fg) = when {
        isRedacted -> Pair(colors.surfaceHover, colors.textMuted)
        isOld -> Pair(if (colors == LightColors) "#fee2e2" else "#2d0a0a", if (colors == LightColors) "#991b1b" else "#fca5a5")
        else -> Pair(if (colors == LightColors) "#dcfce7" else "#0a2d14", if (colors == LightColors) "#166534" else "#86efac")
    }
    Span({
        style {
            fontSize(Typography.sm)
            color(Color(fg))
            backgroundColor(Color(bg))
            borderRadius(BorderRadius.sm)
            padding(2.px, 6.px)
            property("word-break", "break-word")
            property("font-style", if (isRedacted) "italic" else "normal")
            property("font-family", if (!isRedacted) "monospace" else "inherit")
        }
    }) { Text(if (value.isBlank()) "—" else value) }
}

@Composable
private fun SnapshotSection(actionType: String, payload: JsonObject?) {
    val colors = AppTheme.colors
    val snapshot = payload?.get("snapshot") as? JsonObject

    SectionHeader(if (actionType.lowercase() == "create") "Created Snapshot" else "Deleted Snapshot")

    if (snapshot == null || snapshot.isEmpty()) {
        EmptyPayloadNote("No snapshot recorded.")
        return
    }

    Div({
        style {
            border(1.px, LineStyle.Solid, Color(colors.border))
            borderRadius(BorderRadius.md)
            overflow("hidden")
        }
    }) {
        snapshot.entries.forEachIndexed { idx, (key, value) ->
            if (idx > 0) {
                Div({ style { height(1.px); backgroundColor(Color(colors.border)) } })
            }
            val strValue = jsonValueToString(value)
            val isRedacted = strValue == "[REDACTED]"

            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "140px 1fr")
                    padding(Spacing.md, Spacing.lg)
                    alignItems(AlignItems.FlexStart)
                }
            }) {
                Span({
                    style {
                        fontSize(Typography.sm)
                        fontWeight("600")
                        color(Color(colors.textMuted))
                    }
                }) { Text(key) }
                Span({
                    style {
                        fontSize(Typography.sm)
                        color(Color(if (isRedacted) colors.textMuted else colors.text))
                        property("word-break", "break-word")
                        property("font-style", if (isRedacted) "italic" else "normal")
                        property("font-family", if (!isRedacted) "monospace" else "inherit")
                    }
                }) { Text(if (strValue.isBlank()) "—" else strValue) }
            }
        }
    }
}

@Composable
private fun ImportSummarySection(payload: JsonObject?) {
    val colors = AppTheme.colors

    SectionHeader("Import Summary")

    if (payload == null) {
        EmptyPayloadNote("No import data recorded.")
        return
    }

    val filename = (payload["filename"] as? JsonPrimitive)?.content
    val total = (payload["total"] as? JsonPrimitive)?.intOrNull
    val succeeded = (payload["succeeded"] as? JsonPrimitive)?.intOrNull
    val failed = (payload["failed"] as? JsonPrimitive)?.intOrNull
    val errors = payload["errors"] as? JsonArray

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "auto 1fr")
            property("gap", "${Spacing.sm} ${Spacing.lg}")
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.md)
            padding(Spacing.lg)
            marginBottom(Spacing.lg)
            themeTransition()
        }
    }) {
        if (filename != null) MetaRow("File", filename, colors.text)
        if (total != null) MetaRow("Total", "$total rows", colors.text)
        if (succeeded != null) MetaRow("Succeeded", "$succeeded", colors.success)
        if (failed != null) MetaRow("Failed", "$failed", if (failed > 0) colors.danger else colors.textMuted)
    }

    if (errors != null && errors.isNotEmpty()) {
        Span({
            style {
                display(DisplayStyle.Block)
                fontSize(Typography.sm)
                fontWeight("600")
                color(Color(colors.danger))
                marginBottom(Spacing.sm)
            }
        }) { Text("Errors (${errors.size})") }

        Div({
            style {
                backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#2d0a0a"))
                borderRadius(BorderRadius.md)
                padding(Spacing.md, Spacing.lg)
                property("max-height", "200px")
                overflow("auto")
            }
        }) {
            errors.forEach { err ->
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        paddingBottom(Spacing.xs)
                        property("font-family", "monospace")
                    }
                }) { Text(jsonValueToString(err)) }
            }
        }
    }
}

@Composable
private fun RawPayloadSection(payload: JsonObject) {
    SectionHeader("Payload")
    val colors = AppTheme.colors
    Pre({
        style {
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.md)
            padding(Spacing.lg)
            fontSize(Typography.sm)
            color(Color(colors.text))
            overflow("auto")
            property("white-space", "pre-wrap")
            property("word-break", "break-word")
            property("font-family", "monospace")
        }
    }) {
        Text(Json.encodeToString(JsonObject.serializer(), payload))
    }
}

@Composable
private fun SessionMetadataSection(sessionIp: String) {
    val colors = AppTheme.colors
    var expanded by remember { mutableStateOf(false) }

    Div({ style { marginTop(Spacing.xl) } }) {
        Div({
            onClick { expanded = !expanded }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                cursor("pointer")
                marginBottom(if (expanded) Spacing.sm else 0.px)
            }
        }) {
            Span({
                style {
                    fontSize(Typography.sm)
                    fontWeight("600")
                    color(Color(colors.textMuted))
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.05em")
                }
            }) { Text("Session Metadata") }
            Span({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    property("transition", "transform 0.2s ease")
                    property("transform", if (expanded) "rotate(180deg)" else "rotate(0deg)")
                    property("display", "inline-block")
                }
            }) { Text("▾") }
        }

        if (expanded) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "auto 1fr")
                    property("gap", "${Spacing.sm} ${Spacing.lg}")
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.lg)
                    themeTransition()
                }
            }) {
                MetaRow("IP Address", sessionIp, colors.textMuted)
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

@Composable
private fun SectionHeader(text: String) {
    val colors = AppTheme.colors
    Span({
        style {
            display(DisplayStyle.Block)
            fontSize(Typography.sm)
            fontWeight("600")
            color(Color(colors.textMuted))
            property("text-transform", "uppercase")
            property("letter-spacing", "0.05em")
            marginBottom(Spacing.md)
        }
    }) { Text(text) }
}

@Composable
private fun EmptyPayloadNote(message: String) {
    val colors = AppTheme.colors
    Div({
        style {
            color(Color(colors.textMuted))
            fontSize(Typography.sm)
            padding(Spacing.lg)
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.md)
            marginBottom(Spacing.lg)
        }
    }) { Text(message) }
}

@Composable
private fun MetaRow(label: String, value: String, valueColor: String) {
    val colors = AppTheme.colors
    Span({
        style {
            fontSize(Typography.sm)
            fontWeight("600")
            color(Color(colors.textMuted))
            property("white-space", "nowrap")
        }
    }) { Text("$label:") }
    Span({
        style {
            fontSize(Typography.sm)
            color(Color(valueColor))
            property("word-break", "break-word")
        }
    }) { Text(value) }
}

private fun jsonValueToString(element: JsonElement): String = when (element) {
    is JsonPrimitive -> element.content
    is JsonNull -> "null"
    is JsonObject -> Json.encodeToString(JsonObject.serializer(), element)
    is JsonArray -> Json.encodeToString(JsonArray.serializer(), element)
}

private fun formatTimestamp(iso: String): String = try {
    val t = iso.replace("T", " ").substringBefore(".")
    t.substringBeforeLast(":").takeLast(16)
} catch (_: Throwable) {
    iso.take(16)
}
